import { useParams, Link } from "react-router-dom";
import { CheckCircle2, CreditCard, LogIn, ShieldCheck, Store, UserCheck } from "lucide-react";
import { money, validityLabel } from "@/lib/api";
import { ErrorBox, Logo, Spinner, useFetch } from "@/components/common";

const STEPS = [
  { icon: Store, title: "Buy your card at a GIA cashier", text: "Pay by cash or QR Ph. The cashier activates your card and records your email." },
  { icon: CreditCard, title: "Scan & enter your card serial", text: "Open GIA CARE+, tap Register, scan this QR and enter the printed serial." },
  { icon: UserCheck, title: "Verify your email", text: "Confirm the code sent to the email you gave the cashier, then set your password." },
  { icon: ShieldCheck, title: "Use your benefits", text: "Open the app to see your digital card and book services." },
];

export function BenefitList({ benefits }) {
  return (
    <ul className="space-y-2.5">
      {benefits.map((b) => (
        <li key={b.id} className="flex gap-3 rounded-2xl border border-slate-100 bg-white p-3.5" data-testid={`benefit-item-${b.code}`}>
          <CheckCircle2 className="mt-0.5 shrink-0 text-purple-600" size={18} />
          <div>
            <p className="text-sm font-bold text-slate-900">{b.quantity != null && b.benefit_type !== "INSURANCE" ? `${b.quantity}× ` : ""}{b.name}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{b.description}</p>
            {b.eligible_relationships?.length > 1 && (
              <p className="mt-1 text-[11px] font-medium text-blue-700">For: {b.eligible_relationships.map((r) => (r === "SELF" ? "Cardholder" : r.toLowerCase())).join(", ")}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function Enroll() {
  const { slug } = useParams();
  const { data: pkg, error, loading } = useFetch(`/public/packages/${slug}`);
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/60 via-white to-white">
      <div className="mx-auto max-w-xl px-5 py-8">
        <div className="mb-6 flex items-center gap-3">
          <Logo size={44} />
          <span className="text-lg font-extrabold text-gradient">GIA CARE+</span>
        </div>
        {loading && <Spinner />}
        <ErrorBox error={error} />
        {pkg && (
          <div className="rise space-y-6" data-testid="enroll-package">
            <div className="overflow-hidden rounded-3xl gradient-card p-6 text-white shadow-xl shadow-purple-900/20">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/70">Package</p>
              <h1 className="mt-1 text-3xl font-extrabold tracking-tight" data-testid="enroll-package-name">{pkg.name}</h1>
              <p className="mt-2 text-sm leading-relaxed text-white/80">{pkg.description}</p>
              <div className="mt-5 flex items-end gap-3">
                <span className="text-3xl font-extrabold" data-testid="enroll-package-price">{money(pkg.price, pkg.currency)}</span>
                <span className="pb-1 text-sm text-white/70">valid {validityLabel(pkg.validity_value, pkg.validity_unit)} after activation</span>
              </div>
            </div>
            <section>
              <h2 className="mb-3 text-base font-bold">What's included</h2>
              <BenefitList benefits={pkg.benefits} />
            </section>
            {pkg.providers?.length > 0 && (
              <p className="text-xs text-slate-500">Care provided by: <span className="font-semibold text-slate-700">{pkg.providers.map((p) => p.name).join(", ")}</span></p>
            )}
            <section className="surface p-5">
              <h2 className="mb-4 text-base font-bold">How to enroll</h2>
              <ol className="space-y-4">
                {STEPS.map(({ icon: Icon, title, text }, i) => (
                  <li key={title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-700"><Icon size={17} /></span>
                    <div><p className="text-sm font-semibold">{i + 1}. {title}</p><p className="text-xs text-slate-500">{text}</p></div>
                  </li>
                ))}
              </ol>
            </section>
            {pkg.terms && <p className="text-[11px] leading-relaxed text-slate-400">{pkg.terms}</p>}
            <Link to={`/register?package=${pkg.slug}`} className="btn-primary w-full py-3" data-testid="enroll-register-button">Register my activated card</Link>
            <Link to="/login" className="btn-secondary w-full py-3" data-testid="enroll-signin-button"><LogIn size={16} /> Already registered — Sign in</Link>
            <p className="text-center text-[11px] text-slate-400">Scanning this QR alone does not activate benefits.</p>
          </div>
        )}
      </div>
    </div>
  );
}
